export const REQUEST_SCOPE = Object.freeze({
	TENANT: 'tenant',
	IDENTITY: 'identity',
	ORGANIZATION_CONTEXT: 'organization-context',
	PUBLIC: 'public',
	CONTROL_PLANE: 'control-plane',
	EXTERNAL: 'external',
	UNCLASSIFIED: 'unclassified',
})

const methodIs = (...methods) => new Set(methods)
const ANY = null

// Keep this table aligned with the backend route inventory. First match wins,
// so narrow public/identity exceptions precede private data-plane families.
export const API_REQUEST_FAMILIES = Object.freeze([
	{ family: 'organization-context-bootstrap', scope: REQUEST_SCOPE.ORGANIZATION_CONTEXT, methods: methodIs('GET'), path: /^\/api\/organization-context\/?$/ },
	{ family: 'organization-list', scope: REQUEST_SCOPE.IDENTITY, methods: methodIs('GET'), path: /^\/api\/organizations\/?$/ },
	{ family: 'team-create', scope: REQUEST_SCOPE.IDENTITY, methods: methodIs('POST'), path: /^\/api\/organizations\/teams\/?$/ },
	{ family: 'invitation-acceptance', scope: REQUEST_SCOPE.IDENTITY, methods: methodIs('POST'), path: /^\/api\/organization-invitations\/[^/]+\/accept\/?$/ },
	{ family: 'incoming-invitations', scope: REQUEST_SCOPE.IDENTITY, methods: methodIs('GET'), path: /^\/api\/organization-invitations\/incoming\/?$/ },
	{ family: 'organization-discovery', scope: REQUEST_SCOPE.IDENTITY, methods: methodIs('GET'), path: /^\/api\/organization-discovery\/?$/ },
	{ family: 'authentication', scope: REQUEST_SCOPE.IDENTITY, methods: ANY, path: /^\/auth(?:\/|$)/ },
	{ family: 'identity-profile-and-catalogs', scope: REQUEST_SCOPE.IDENTITY, methods: ANY, path: /^(?:\/translations\/|\/locales\/|\/predefined_data\/|\/fields\/|\/userinfo\/|\/api\/parser\/)/ },
	{ family: 'identity-profile-and-catalogs', scope: REQUEST_SCOPE.IDENTITY, methods: ANY, path: /^(?:\/api\/dadata\/suggest|\/api\/user(?:\/.*)?|\/api\/languages|\/api\/countries|\/categories\/ru|\/user\/by-email|\/descriptionAi)\/?$/ },
	{ family: 'development-diagnostic', scope: REQUEST_SCOPE.IDENTITY, methods: methodIs('GET'), path: /^\/api\/test\/?$/ },

	{ family: 'public-request', scope: REQUEST_SCOPE.PUBLIC, methods: methodIs('GET'), path: /^\/requests\/[^/]+\/?$/ },
	{ family: 'public-request-feedback', scope: REQUEST_SCOPE.PUBLIC, methods: methodIs('PUT'), path: /^\/requests\/[^/]+\/objects\/[^/]+\/client-feedback\/[^/]+\/?$/ },
	{ family: 'public-object', scope: REQUEST_SCOPE.PUBLIC, methods: methodIs('GET'), path: /^\/objects\/[^/]+\/?$/ },
	{ family: 'public-showcase', scope: REQUEST_SCOPE.PUBLIC, methods: methodIs('GET'), path: /^\/showcase\/[^/]+\/?$/ },
	{ family: 'public-classified-feed', scope: REQUEST_SCOPE.PUBLIC, methods: methodIs('GET', 'HEAD'), path: /^\/public\/classified-feeds\/avito\/[^/]+\.xml\/?$/ },
	{ family: 'public-catalog-and-media', scope: REQUEST_SCOPE.PUBLIC, methods: ANY, path: /^(?:\/objects_open|\/dadata_open\/suggest|\/open-photos\/|\/categories_open\/ru|\/jsonfiles)(?:\/|$)/ },

	{ family: 'platform-control-plane', scope: REQUEST_SCOPE.CONTROL_PLANE, methods: ANY, path: /^\/api\/(?:admin|impersonation)(?:\/|$)/ },
	{ family: 'billing-provider-callback', scope: REQUEST_SCOPE.PUBLIC, methods: methodIs('GET', 'POST'), path: /^\/api\/billing\/providers\/robokassa\/result\/?$/ },

	{ family: 'organization-management', scope: REQUEST_SCOPE.TENANT, methods: ANY, path: /^\/api\/(?:organization-profile|organization-memberships|organization-invitations)(?:\/|$)/ },
	{ family: 'custom-fields', scope: REQUEST_SCOPE.TENANT, methods: ANY, path: /^\/api\/(?:custom-field-definitions|objects\/[^/]+\/custom-fields)(?:\/|$)/ },
	{ family: 'resource-assignment', scope: REQUEST_SCOPE.TENANT, methods: methodIs('PUT'), path: /^\/api\/[^/]+\/[^/]+\/assignee\/?$/ },
	{ family: 'core-clients-and-assignment', scope: REQUEST_SCOPE.TENANT, methods: ANY, path: /^\/api\/(?:clients|(?:objects|requests|clients)\/[^/]+\/assignee)(?:\/|$)/ },
	{ family: 'entitlements', scope: REQUEST_SCOPE.TENANT, methods: ANY, path: /^\/api\/entitlements(?:\/|$)/ },
	{ family: 'billing', scope: REQUEST_SCOPE.TENANT, methods: ANY, path: /^\/api\/billing(?:\/|$)/ },
	{ family: 'classified-management', scope: REQUEST_SCOPE.TENANT, methods: ANY, path: /^\/classifieds(?:\/|$)/ },
	{ family: 'object-management-api', scope: REQUEST_SCOPE.TENANT, methods: ANY, path: /^\/api\/objects(?:\/|$)/ },
	{ family: 'legacy-colleague-management', scope: REQUEST_SCOPE.TENANT, methods: ANY, path: /^\/api\/colleague(?:s)?(?:\/|$)/ },
	{ family: 'object-management', scope: REQUEST_SCOPE.TENANT, methods: ANY, path: /^(?:\/objects(?:\/|$)|\/add-new-object(?:-parsed)?\/?$|\/upload-file\/?$|\/upload-edited-file\/?$|\/update-sort-files\/?$|\/delete-object\/|\/rotate-photo\/|\/get-presigned-url\/)/ },
	{ family: 'request-and-kanban', scope: REQUEST_SCOPE.TENANT, methods: ANY, path: /^(?:\/requests(?:\/|$)|\/boards(?:\/|$)|\/columns(?:\/|$)|\/renameColumn\/?$|\/update-column-order\/?$|\/request-fields\/?$)/ },
	{ family: 'tenant-dictionaries', scope: REQUEST_SCOPE.TENANT, methods: ANY, path: /^(?:\/tags|\/statuses|\/request-sources)(?:\/|$)/ },
	{ family: 'showcase-management', scope: REQUEST_SCOPE.TENANT, methods: ANY, path: /^(?:\/user-showcase|\/check-slug)\/?$/ },
])

function absoluteUrl(value) {
	return /^[a-z][a-z\d+.-]*:\/\//i.test(String(value || ''))
}

function requestPath(url) {
	try {
		return new URL(url, 'https://reldo.invalid').pathname
	} catch {
		return String(url || '').split('?')[0]
	}
}

function isExternalAbsoluteUrl(url, baseURL) {
	if (!absoluteUrl(url)) return false
	if (!baseURL) return true
	try {
		return new URL(url).origin !== new URL(baseURL, 'https://reldo.invalid').origin
	} catch {
		return true
	}
}

export function classifyApiRequest(config = {}) {
	if (isExternalAbsoluteUrl(config.url, config.baseURL)) {
		return { scope: REQUEST_SCOPE.EXTERNAL, family: 'external-absolute-url' }
	}
	const path = requestPath(config.url)
	const method = String(config.method || 'get').toUpperCase()
	const match = API_REQUEST_FAMILIES.find(family =>
		(!family.methods || family.methods.has(method)) && family.path.test(path)
	)
	return match
		? { scope: match.scope, family: match.family }
		: { scope: REQUEST_SCOPE.UNCLASSIFIED, family: null }
}

export function isTenantScopedRequest(config) {
	return classifyApiRequest(config).scope === REQUEST_SCOPE.TENANT
}

export function isGenerationBoundRequest(config) {
	const scope = classifyApiRequest(config).scope
	return scope === REQUEST_SCOPE.TENANT || scope === REQUEST_SCOPE.ORGANIZATION_CONTEXT
}
